// Turning Movement Count by Class — three FHWA buckets (Light = Class 1-3,
// Medium = Class 4-7, Heavy = Class 8-13) rendered as stacked sections.
//
// TODO: this report duplicates the lane-line setup flow from
// TurningMovementReportV2 (state, directionGroups memo, conflict resolution,
// street-name detection, lane-selection UI). After the base V2 rename settles,
// extract the shared setup into a useTmcSetup hook + <TmcSetupView /> component.

import {
    AggregationPeriod,
    Button,
    DetectionZoneCardinalDirection,
    EventReports,
    Facilities,
    Facility,
    LaneDirectionJson,
    LaneLine,
    TmcColumnCountByClass,
    TurningMovementCountReportByClassV2,
    TurningMovementReportConfiguration,
    TurnType,
    Vector2,
} from "@app/shared";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useFetch } from "../../hooks";
import {
    calculateCardinalDirection,
    useFacilityCoordsHelper,
    getCardinalDirectionAbbreviation,
    getCardinalDirectionWithBound,
    detectLaneLineStreetName,
} from "../../helpers";
import { IconLink, Icons } from "../shared";
import { DateOptions, IConfiguredTrafficReport, TrafficReportProps } from "./IConfiguredTrafficReport";
import {
    getLaneNumberColor,
    getIntersectionName,
    formatPeakHourLabel,
    calculateRollingPeakHourGeneric,
} from "./TurningMovementReport/TmcHelpers";
import { ReportContext } from "./reportHelpers";
import { IntersectionDiagram } from "./IntersectionDiagram";
import { OmniGoogleMap, OmniMapMethods } from "../shared/maps/OmniGoogleMap";
import { LaneLineMapShape } from "../shared/maps/MapShapes/LaneLineMapShape";
import { DynamicFixedSizeContainer } from "../shared/DynamicFixedSizeContainer";

type ClassKey = "light" | "medium" | "heavy";

const CLASS_SECTIONS: { key: ClassKey; label: string; subLabel: string }[] = [
    { key: "light", label: "Light Vehicles", subLabel: "FHWA Class 1-3 (motorcycles, passenger cars, 2-axle 4-tire single units)" },
    { key: "medium", label: "Medium Vehicles", subLabel: "FHWA Class 4-7 (buses + single-unit trucks)" },
    { key: "heavy", label: "Heavy Vehicles", subLabel: "FHWA Class 8-13 (single- and multi-trailer combinations)" },
];

const getCountForColumn = (row: any, columnKey: string, classKey: ClassKey): number => {
    const countObj = (row.counts as TmcColumnCountByClass[] | undefined)?.find((c) => c.columnKey === columnKey);
    if (!countObj) return 0;
    return (countObj as any)[classKey] || 0;
};

interface LaneLineWithDirection {
    laneLine: LaneLine;
    inferredDirection: DetectionZoneCardinalDirection;
    laneNumber: number;
}

interface GroupedLaneLine {
    laneNumber: number;
    direction: DetectionZoneCardinalDirection;
    turnType: TurnType | undefined | null;
    laneLines: LaneLine[];
    displayName: string;
    detectedStreetName?: string;
}

interface DirectionGroup {
    direction: DetectionZoneCardinalDirection;
    groupedLaneLines: GroupedLaneLine[];
}

const buildDayCSVBlock = (
    classLabel: string,
    dayKey: string,
    dayRows: any[],
    columns: any[],
    columnsByDirection: Map<number, any[]>,
    directionStreetNames: Map<number, string>,
    classKey: ClassKey,
): string => {
    let csv = "";
    csv += `${classLabel} - ${new Date(dayKey).toLocaleDateString()},`;
    columns.forEach(() => (csv += ","));
    csv += "\n";

    csv += ",";
    Array.from(columnsByDirection.entries()).forEach(([dir, cols]) => {
        const streetName = directionStreetNames.get(dir) || "";
        csv += streetName;
        cols.forEach(() => (csv += ","));
    });
    csv += "\n";

    csv += ",";
    Array.from(columnsByDirection.entries()).forEach(([, cols]) => {
        cols.forEach((col: any) => (csv += col.displayLabel + ","));
    });
    csv += "Total\n";

    dayRows.forEach((row) => {
        const time = row.startTime ? new Date(row.startTime).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false }) : "";
        csv += time + ",";

        let rowTotal = 0;
        columns.forEach((col: any) => {
            const count = getCountForColumn(row, col.key, classKey);
            rowTotal += count;
            csv += count + ",";
        });
        csv += rowTotal + "\n";
    });

    csv += "Total,";
    let grandTotal = 0;
    columns.forEach((col: any) => {
        const colTotal = dayRows.reduce((sum: number, row: any) => sum + getCountForColumn(row, col.key, classKey), 0);
        grandTotal += colTotal;
        csv += colTotal + ",";
    });
    csv += grandTotal + "\n";

    const peakResult = calculateRollingPeakHourGeneric(
        dayRows,
        (row) => (row.startTime ? new Date(row.startTime) : null),
        (row) => columns.reduce((sum: number, col: any) => sum + getCountForColumn(row, col.key, classKey), 0),
    );
    if (peakResult.peakStartTime) {
        const peakLabel = formatPeakHourLabel(peakResult.peakStartTime, peakResult.peakEndTime);
        csv += `Peak Hour (${peakLabel}),`;
        columns.forEach((col: any) => {
            const colTotal = peakResult.peakRows.reduce((sum: number, row: any) => sum + getCountForColumn(row, col.key, classKey), 0);
            csv += colTotal + ",";
        });
        csv += peakResult.peakTotal + "\n";
    }

    csv += "\n";
    return csv;
};

const downloadByClassCSV = (
    reportData: TurningMovementCountReportByClassV2,
    directionStreetNames: Map<number, string>,
    startDate: Date,
    endDate: Date,
) => {
    if (!reportData?.counts || reportData.counts.length === 0) return;
    try {
        const columns = reportData.columns || [];
        const columnsByDirection = new Map<number, any[]>();
        columns.forEach((col: any) => {
            if (!columnsByDirection.has(col.direction)) columnsByDirection.set(col.direction, []);
            columnsByDirection.get(col.direction)!.push(col);
        });

        const dataByDay = new Map<string, any[]>();
        reportData.counts.forEach((row: any) => {
            if (row.startTime) {
                const dayKey = new Date(row.startTime).toDateString();
                if (!dataByDay.has(dayKey)) dataByDay.set(dayKey, []);
                dataByDay.get(dayKey)!.push(row);
            }
        });

        let csv = `Turning Movement Count by Class,${startDate.toDateString()} through ${endDate.toDateString()}\n\n`;

        CLASS_SECTIONS.forEach((section) => {
            csv += `=== ${section.label} (${section.subLabel}) ===\n`;
            dataByDay.forEach((dayRows, dayKey) => {
                csv += buildDayCSVBlock(section.label, dayKey, dayRows, columns, columnsByDirection, directionStreetNames, section.key);
            });
            csv += "\n";
        });

        const blob = new Blob([csv], { type: "text/csv" });
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = "TurningMovementCountByClass.csv";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(link.href);
    } catch (error) {
        console.error("Download failed:", error);
    }
};

export const TMCByClassLoader: React.FC<TrafficReportProps> = (props: TrafficReportProps) => {
    const { reportConfig } = useContext(ReportContext);
    const allReady = props && (props.size.height ?? 0) > 10;

    const [facility, isFacilityLoading] = useFetch(async () => {
        if (reportConfig.context.facility) return reportConfig.context.facility;
        if (reportConfig.context.device?.facilityId) {
            return await Facilities.getById(reportConfig.context.device.facilityId);
        }
        return undefined;
    }, [reportConfig.context.facility?.id, reportConfig.context.device?.facilityId]);

    if (!allReady || isFacilityLoading) return <div className="p-4">Loading...</div>;
    if (!facility) return <div className="p-4">No facility found for this device.</div>;
    return <TMCByClass {...props} loadedFacility={facility} />;
};

interface TMCByClassProps extends TrafficReportProps {
    loadedFacility: Facility;
}

export const TMCByClass: React.FC<TMCByClassProps> = (props) => {
    const { reportConfig } = useContext(ReportContext);
    const [selectedLaneLineIds, setSelectedLaneLineIds] = useState<Set<string>>(new Set());
    const [conflictResolutions, setConflictResolutions] = useState<Map<number, DetectionZoneCardinalDirection>>(new Map());
    const [reportData, setReportData] = useState<TurningMovementCountReportByClassV2 | null>(null);
    const [isLoadingReport, setIsLoadingReport] = useState(false);
    const [reportError, setReportError] = useState<string | null>(null);
    const [showStreetNames, setShowStreetNames] = useState(false);
    const [isDetectingStreetNames, setIsDetectingStreetNames] = useState(false);
    const [streetNamesDetected, setStreetNamesDetected] = useState(false);
    const [selectedHourByDayClass, setSelectedHourByDayClass] = useState<Map<string, number>>(new Map());
    const [collapsedPanels, setCollapsedPanels] = useState<Set<string>>(new Set());
    // Tracks whether a report has been generated, so a date-range change can
    // auto-refetch without firing on first mount or while still in setup.
    const reportGeneratedRef = useRef(false);
    const mapRef = useRef<OmniMapMethods>(null);

    const facility = props.loadedFacility;

    const [facilityLaneLines, isLaneLinesLoading] = useFetch(async () => {
        if (facility?.laneLines) return facility.laneLines;
        if (!facility?.id) return [];
        const fetched = await Facilities.getById(facility.id);
        return fetched?.laneLines || [];
    }, [facility?.id]);

    const laneLines = facilityLaneLines || [];
    const coordsHelper = useFacilityCoordsHelper(facility);

    const directionGroups = useMemo<DirectionGroup[]>(() => {
        if (!laneLines || !coordsHelper) return [];

        const laneLinesWithDirection: LaneLineWithDirection[] = laneLines
            .filter((ll) => ll.points && ll.points.length >= 2)
            .map((ll) => ({
                laneLine: ll,
                inferredDirection: calculateCardinalDirection(ll.points![0], ll.points![1], coordsHelper),
                laneNumber: ll.laneNumber || 0,
            }));

        const byDirection = new Map<DetectionZoneCardinalDirection, LaneLineWithDirection[]>();
        laneLinesWithDirection.forEach((llwd) => {
            if (!byDirection.has(llwd.inferredDirection)) byDirection.set(llwd.inferredDirection, []);
            byDirection.get(llwd.inferredDirection)!.push(llwd);
        });

        const result: DirectionGroup[] = [];
        byDirection.forEach((lls, direction) => {
            const combos = new Map<string, GroupedLaneLine>();
            lls.forEach((llwd) => {
                const key = `${llwd.laneNumber}-${llwd.laneLine.turnType ?? "null"}`;
                if (!combos.has(key)) {
                    combos.set(key, {
                        laneNumber: llwd.laneNumber,
                        direction,
                        turnType: llwd.laneLine.turnType,
                        laneLines: [],
                        displayName: llwd.laneLine.name || "Unnamed",
                    });
                }
                combos.get(key)!.laneLines.push(llwd.laneLine);
            });

            const groupedLaneLines = Array.from(combos.values()).sort((a, b) => {
                if (a.laneNumber === 0 && b.laneNumber !== 0) return 1;
                if (a.laneNumber !== 0 && b.laneNumber === 0) return -1;
                if (a.laneNumber !== b.laneNumber) return a.laneNumber - b.laneNumber;
                return (a.turnType ?? -1) - (b.turnType ?? -1);
            });

            result.push({ direction, groupedLaneLines });
        });

        result.sort((a, b) => a.direction - b.direction);
        return result;
    }, [laneLines, coordsHelper]);

    const validation = useMemo(() => {
        const laneNumberToDirections = new Map<number, Set<DetectionZoneCardinalDirection>>();
        const unassignedGroups: GroupedLaneLine[] = [];

        directionGroups.forEach((dirGroup) => {
            dirGroup.groupedLaneLines.forEach((group) => {
                if (group.laneNumber === 0) {
                    unassignedGroups.push(group);
                } else {
                    if (!laneNumberToDirections.has(group.laneNumber)) {
                        laneNumberToDirections.set(group.laneNumber, new Set());
                    }
                    laneNumberToDirections.get(group.laneNumber)!.add(dirGroup.direction);
                }
            });
        });

        const actualConflicts = new Map<number, string[]>();
        const conflictDirectionOptions = new Map<number, DetectionZoneCardinalDirection[]>();
        laneNumberToDirections.forEach((directions, laneNum) => {
            if (directions.size > 1) {
                actualConflicts.set(laneNum, Array.from(directions).map((d) => getCardinalDirectionAbbreviation(d) || "Unknown"));
                conflictDirectionOptions.set(laneNum, Array.from(directions));
            }
        });

        return {
            hasConflicts: actualConflicts.size > 0,
            conflicts: actualConflicts,
            conflictDirectionOptions,
            hasUnassigned: unassignedGroups.length > 0,
            unassignedGroups,
        };
    }, [directionGroups]);

    const detectStreetNamesForGroups = async () => {
        if (!coordsHelper || directionGroups.length === 0) return;
        setIsDetectingStreetNames(true);
        try {
            for (const dirGroup of directionGroups) {
                for (const group of dirGroup.groupedLaneLines) {
                    if (group.laneLines.length === 0) continue;
                    const detected = await detectLaneLineStreetName(group.laneLines[0], dirGroup.direction, coordsHelper);
                    if (detected) group.detectedStreetName = detected.formattedName;
                }
            }
            setStreetNamesDetected(true);
        } catch (error) {
            console.error("Error detecting street names:", error);
        } finally {
            setIsDetectingStreetNames(false);
        }
    };

    useEffect(() => {
        if (showStreetNames && !isDetectingStreetNames) detectStreetNamesForGroups();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showStreetNames]);

    useEffect(() => {
        if (directionGroups.length > 0 && selectedLaneLineIds.size === 0) {
            const allIds = new Set<string>();
            directionGroups.forEach((dirGroup) => {
                dirGroup.groupedLaneLines.forEach((group) => {
                    group.laneLines.forEach((ll) => {
                        if (ll.id) allIds.add(ll.id);
                    });
                });
            });
            setSelectedLaneLineIds(allIds);
        }
    }, [directionGroups, selectedLaneLineIds.size]);

    const toggleGroup = (group: GroupedLaneLine) => {
        const newSet = new Set(selectedLaneLineIds);
        const groupIds = group.laneLines.map((ll) => ll.id).filter((id) => id) as string[];
        const allSelected = groupIds.every((id) => newSet.has(id));
        if (allSelected) groupIds.forEach((id) => newSet.delete(id));
        else groupIds.forEach((id) => newSet.add(id));
        setSelectedLaneLineIds(newSet);
    };

    const toggleDirection = (direction: DetectionZoneCardinalDirection) => {
        const dirGroup = directionGroups.find((g) => g.direction === direction);
        if (!dirGroup) return;
        const newSet = new Set(selectedLaneLineIds);
        const allIds: string[] = [];
        dirGroup.groupedLaneLines.forEach((group) => {
            group.laneLines.forEach((ll) => {
                if (ll.id) allIds.push(ll.id);
            });
        });
        const allSelected = allIds.every((id) => newSet.has(id));
        if (allSelected) allIds.forEach((id) => newSet.delete(id));
        else allIds.forEach((id) => newSet.add(id));
        setSelectedLaneLineIds(newSet);
    };

    // Runs the API call against the currently selected lanes and date range.
    // No street-name detection here — that happens once in handleGenerateReport.
    const fetchReport = async () => {
        setIsLoadingReport(true);
        setReportError(null);
        try {
            const laneDirections: LaneDirectionJson[] = [];
            directionGroups.forEach((dirGroup) => {
                dirGroup.groupedLaneLines.forEach((group) => {
                    const hasSelected = group.laneLines.some((ll) => ll.id && selectedLaneLineIds.has(ll.id));
                    if (hasSelected && group.laneNumber !== 0) {
                        laneDirections.push({
                            lane: group.laneNumber,
                            direction: dirGroup.direction,
                            turnType: group.turnType ?? undefined,
                        });
                    }
                });
            });

            if (laneDirections.length === 0) {
                setReportError("Please select at least one lane to generate the report");
                return;
            }

            const deviceIds: string[] =
                reportConfig.deviceIds && reportConfig.deviceIds.length > 0
                    ? reportConfig.deviceIds
                    : reportConfig.context.device?.id
                    ? [reportConfig.context.device.id]
                    : [];
            const facilityIds: string[] = facility?.id ? [facility.id] : [];

            const config: TurningMovementReportConfiguration = {
                deviceIds,
                facilityIds,
                inclusiveStart: reportConfig.inclusiveStart!,
                exclusiveEnd: reportConfig.exclusiveEnd!,
                lanes: laneDirections,
                streetNames: [],
                filterTypes: undefined,
                filterClasses: undefined,
                grouping: 0,
                groupingValues: [],
            };

            const result = await EventReports.getTurningMovementCountsByClassV2(AggregationPeriod.FiveMinutes, config);
            setReportData(result ?? null);
            reportGeneratedRef.current = true;
        } catch (error: any) {
            console.error("Error generating report:", error);
            setReportError(error.message || "Failed to generate report");
        } finally {
            setIsLoadingReport(false);
        }
    };

    const handleGenerateReport = async () => {
        if (!streetNamesDetected) await detectStreetNamesForGroups();
        await fetchReport();
        setTimeout(() => {
            window.scrollTo({ top: 0, behavior: "smooth" });
            document.documentElement.scrollTop = 0;
            document.body.scrollTop = 0;
            const scrollableParent = document.querySelector(".print-area")?.closest('[class*="overflow"]');
            if (scrollableParent) scrollableParent.scrollTop = 0;
        }, 100);
    };

    // Once a report exists, changing the date range (preset or custom) refetches
    // in place with the same lane selection.
    useEffect(() => {
        if (!reportGeneratedRef.current) return;
        void fetchReport();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reportConfig.inclusiveStart?.getTime(), reportConfig.exclusiveEnd?.getTime()]);

    if (isLaneLinesLoading) return <div className="p-4">Loading lane lines...</div>;

    if (laneLines.length === 0) {
        return (
            <div className="p-4">
                <div className="border rounded p-4">
                    <p>No lane lines found for this facility. Please create lane lines first.</p>
                </div>
            </div>
        );
    }

    if (reportData && !isLoadingReport) {
        const columns = reportData.columns || [];
        const columnsByDirection = new Map<number, any[]>();
        columns.forEach((col: any) => {
            if (!columnsByDirection.has(col.direction)) columnsByDirection.set(col.direction, []);
            columnsByDirection.get(col.direction)!.push(col);
        });

        const directionStreetNames = new Map<number, string>();
        directionGroups.forEach((dirGroup) => {
            const firstLaneWithStreet = dirGroup.groupedLaneLines.find((g) => g.detectedStreetName && g.laneLines.length > 0);
            if (firstLaneWithStreet?.detectedStreetName) {
                let streetName: string | null = null;
                let match = firstLaneWithStreet.detectedStreetName.match(/\(.*?\)\s+(.+?)\s+-\s+/);
                if (match && match[1]) streetName = match[1];
                if (!streetName) {
                    match = firstLaneWithStreet.detectedStreetName.match(/^(.+?)\s+-\s+/);
                    if (match && match[1]) streetName = match[1];
                }
                if (!streetName && firstLaneWithStreet.detectedStreetName.includes(" - ")) {
                    const parts = firstLaneWithStreet.detectedStreetName.split(" - ");
                    if (parts.length >= 2) streetName = parts[0].replace(/^\(.*?\)\s+/, "").trim();
                }
                if (streetName) directionStreetNames.set(dirGroup.direction, streetName);
            }
        });
        Array.from(columnsByDirection.keys()).forEach((direction) => {
            if (!directionStreetNames.has(direction)) {
                const dirGroup = directionGroups.find((dg) => dg.direction === direction);
                if (dirGroup && dirGroup.groupedLaneLines.length > 0) {
                    for (const group of dirGroup.groupedLaneLines) {
                        for (const ll of group.laneLines) {
                            if (ll.name && ll.name.length > 0) {
                                directionStreetNames.set(direction, ll.name);
                                break;
                            }
                        }
                        if (directionStreetNames.has(direction)) break;
                    }
                }
            }
        });

        const dataByDay = new Map<string, any[]>();
        reportData.counts?.forEach((row: any) => {
            if (row.startTime) {
                const dayKey = new Date(row.startTime).toDateString();
                if (!dataByDay.has(dayKey)) dataByDay.set(dayKey, []);
                dataByDay.get(dayKey)!.push(row);
            }
        });

        const intersectionName = getIntersectionName(directionStreetNames);
        const startDate = reportConfig.inclusiveStart;
        const endDate = reportConfig.exclusiveEnd;

        return (
            <div className="p-4">
                <div className="print:hidden w-full flex flex-row mb-4">
                    <div className="flex-grow">
                        <button onClick={() => { reportGeneratedRef.current = false; setReportData(null); }}>
                            <IconLink Icon={Icons.ArrowBack}>Go Back</IconLink>
                        </button>
                    </div>
                    <div>
                        <button onClick={() => downloadByClassCSV(reportData, directionStreetNames, startDate!, endDate!)}>
                            <IconLink Icon={Icons.Download}>Download CSV</IconLink>
                        </button>
                    </div>
                </div>

                <div className="text-center py-5">
                    {intersectionName && <h1 className="text-2xl font-bold">{intersectionName}</h1>}
                    <h2 className="text-xl">Turning Movement Count by Class</h2>
                    <h3 className="text-gray-600">
                        {startDate?.toDateString()} through {endDate?.toDateString()}
                    </h3>
                </div>

                <div className="space-y-12">
                    {Array.from(dataByDay.entries()).map(([dayKey, dayRows]) => (
                        <DayPanel
                            key={dayKey}
                            dayKey={dayKey}
                            dayRows={dayRows}
                            columns={columns}
                            columnsByDirection={columnsByDirection}
                            directionStreetNames={directionStreetNames}
                            selectedHourByDayClass={selectedHourByDayClass}
                            setSelectedHourByDayClass={setSelectedHourByDayClass}
                            collapsedPanels={collapsedPanels}
                            setCollapsedPanels={setCollapsedPanels}
                        />
                    ))}
                </div>
            </div>
        );
    }

    // Setup view
    return (
        <div className="p-4">
            <h2 className="text-2xl font-bold mb-4">Turning Movement Count by Class</h2>
            <p className="text-gray-600 mb-4">
                Select the lane lines you want to include. The report shows three FHWA-class breakdowns (Light, Medium, Heavy) side-by-side.
            </p>

            <div className="mb-6 border border-gray-300 rounded-lg p-4 bg-gray-50 print:hidden">
                <h3 className="text-lg font-semibold mb-4">Lane Lines Visualization</h3>
                <DynamicFixedSizeContainer getHeight={() => 600} getWidth={() => window.innerWidth - 300}>
                    {({ width, height }) => (
                        <div style={{ width, height }} className="flex flex-col">
                            <div style={{ flex: 1, minHeight: 0, position: "relative" }}>
                                <OmniGoogleMap ref={mapRef} center={coordsHelper.toLatLng(new Vector2({ x: 0, y: 0 }))} height={height - 100} width={width}>
                                    {laneLines.map((ll) => (
                                        <LaneLineMapShape key={ll.id} facility={facility} shape={ll} isEditable={false} isSelectable={false} color={getLaneNumberColor(ll.laneNumber)} showLabel={true} />
                                    ))}
                                </OmniGoogleMap>
                            </div>
                            <div className="mt-3 p-3 bg-white border border-gray-300 rounded">
                                <h4 className="text-sm font-semibold mb-2">Lane Number Colors:</h4>
                                <div className="flex flex-wrap gap-4">
                                    {(() => {
                                        const uniqueLaneNumbers = Array.from(new Set(laneLines.map((ll) => ll.laneNumber).filter((n) => n !== null && n !== undefined))).sort((a, b) => a - b);
                                        return uniqueLaneNumbers.map((laneNum) => (
                                            <div key={laneNum} className="flex items-center gap-2">
                                                <div className="w-6 h-6 rounded border border-gray-400" style={{ backgroundColor: getLaneNumberColor(laneNum) }}></div>
                                                <span className="text-sm font-medium">Lane {laneNum}</span>
                                            </div>
                                        ));
                                    })()}
                                </div>
                            </div>
                        </div>
                    )}
                </DynamicFixedSizeContainer>
            </div>

            {validation.hasConflicts && (
                <div className="bg-red-50 border border-red-300 rounded p-4 mb-4">
                    <h3 className="text-red-800 font-semibold mb-3">
                        <i className="fa fa-exclamation-triangle mr-2"></i>
                        Lane Number Conflicts Detected
                    </h3>
                    <p className="text-red-700 text-sm mb-3">The same lane number is assigned to multiple directions. Please choose which direction to use for each conflicted lane number.</p>
                    <div className="space-y-3">
                        {Array.from(validation.conflicts.entries()).map(([laneNum, directions]) => {
                            const directionOptions = validation.conflictDirectionOptions.get(laneNum) || [];
                            const selectedDirection = conflictResolutions.get(laneNum);
                            return (
                                <div key={laneNum} className="bg-white border border-red-200 rounded p-3">
                                    <div className="flex items-center gap-4">
                                        <span className="text-red-800 font-medium whitespace-nowrap">Lane {laneNum}:</span>
                                        <span className="text-red-700 text-sm flex-shrink">Appears in {directions.join(", ")}</span>
                                        <div className="flex items-center gap-2 ml-auto">
                                            <label className="text-red-700 text-sm whitespace-nowrap">Use direction:</label>
                                            <select
                                                value={selectedDirection ?? ""}
                                                onChange={(e) => {
                                                    const newResolutions = new Map(conflictResolutions);
                                                    if (e.target.value) newResolutions.set(laneNum, parseInt(e.target.value) as DetectionZoneCardinalDirection);
                                                    else newResolutions.delete(laneNum);
                                                    setConflictResolutions(newResolutions);
                                                }}
                                                className="border border-red-300 rounded px-3 py-1 text-sm bg-white"
                                            >
                                                <option value="">-- Select Direction --</option>
                                                {directionOptions.map((dir) => (
                                                    <option key={dir} value={dir}>
                                                        {getCardinalDirectionAbbreviation(dir)}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {validation.hasUnassigned && (
                <div className="bg-red-50 border border-red-300 rounded p-4 mb-4">
                    <h3 className="text-red-800 font-semibold mb-2">
                        <i className="fa fa-exclamation-circle mr-2"></i>
                        Lane Lines Without Numbers
                    </h3>
                    <p className="text-red-700 text-sm mb-2">{validation.unassignedGroups.length} lane line group(s) don't have lane numbers assigned. These lanes won't track any targets.</p>
                </div>
            )}

            <div className="mb-4">
                <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={showStreetNames} onChange={(e) => setShowStreetNames(e.target.checked)} className="cursor-pointer h-4 w-4" disabled={isDetectingStreetNames} />
                    <span className="text-sm font-medium text-gray-700">
                        Show Detected Street Names
                        {isDetectingStreetNames && <i className="fa fa-spinner fa-spin ml-2"></i>}
                    </span>
                </label>
            </div>

            <div className="space-y-6">
                {directionGroups.map((dirGroup) => {
                    const allDirIds: string[] = [];
                    dirGroup.groupedLaneLines.forEach((group) => {
                        group.laneLines.forEach((ll) => {
                            if (ll.id) allDirIds.push(ll.id);
                        });
                    });
                    const allDirSelected = allDirIds.every((id) => selectedLaneLineIds.has(id));
                    const someDirSelected = allDirIds.some((id) => selectedLaneLineIds.has(id));
                    return (
                        <div key={dirGroup.direction} className="border rounded-lg p-4 bg-white shadow-sm">
                            <div className="flex items-center mb-3">
                                <input
                                    type="checkbox"
                                    checked={allDirSelected}
                                    ref={(input) => {
                                        if (input) input.indeterminate = someDirSelected && !allDirSelected;
                                    }}
                                    onChange={() => toggleDirection(dirGroup.direction)}
                                    className="mr-3 h-5 w-5 cursor-pointer"
                                />
                                <h3 className="text-lg font-semibold">
                                    <i className="fa fa-compass mr-2"></i>
                                    {getCardinalDirectionWithBound(dirGroup.direction)}
                                    <span className="text-sm text-gray-500 ml-2">
                                        ({dirGroup.groupedLaneLines.length} unique lane{dirGroup.groupedLaneLines.length !== 1 ? "s" : ""})
                                    </span>
                                </h3>
                            </div>
                            <div className="ml-8 space-y-2">
                                {dirGroup.groupedLaneLines.map((group, idx) => {
                                    const isConflicted = group.laneNumber !== 0 && validation.conflicts.has(group.laneNumber);
                                    const isUnassigned = group.laneNumber === 0;
                                    const chosenDirection = conflictResolutions.get(group.laneNumber);
                                    const isExcludedByConflictResolution = isConflicted && chosenDirection !== undefined && chosenDirection !== dirGroup.direction;
                                    const groupIds = group.laneLines.map((ll) => ll.id).filter((id) => id) as string[];
                                    const allGroupSelected = groupIds.every((id) => selectedLaneLineIds.has(id));
                                    const displayName = showStreetNames && group.detectedStreetName ? group.detectedStreetName : group.displayName;
                                    return (
                                        <label
                                            key={`${group.laneNumber}-${group.turnType}-${idx}`}
                                            className={`flex items-center cursor-pointer p-2 rounded ${
                                                isExcludedByConflictResolution
                                                    ? "bg-gray-100 opacity-50 cursor-not-allowed"
                                                    : isConflicted || isUnassigned
                                                    ? "bg-red-50 hover:bg-red-100 border border-red-200"
                                                    : "hover:bg-gray-50"
                                            }`}
                                        >
                                            <input type="checkbox" checked={allGroupSelected} onChange={() => toggleGroup(group)} disabled={isExcludedByConflictResolution} className="mr-3 h-4 w-4 cursor-pointer" />
                                            <span className={`font-medium mr-2 ${isConflicted || isUnassigned ? "text-red-700" : "text-gray-700"}`}>
                                                {isConflicted && <i className="fa fa-exclamation-triangle mr-1"></i>}
                                                {isUnassigned ? "No Lane #" : `Lane ${group.laneNumber}`}:
                                            </span>
                                            <span className="text-gray-600 flex-1">{displayName}</span>
                                            {group.laneLines.length > 1 && <span className="text-xs text-gray-500 ml-2">({group.laneLines.length} lane lines merged)</span>}
                                        </label>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="mt-6 flex gap-4 items-center">
                <Button
                    onClick={handleGenerateReport}
                    disabled={selectedLaneLineIds.size === 0 || (validation.hasConflicts && conflictResolutions.size !== validation.conflicts.size)}
                    className="px-6 py-3"
                >
                    <i className="fa fa-chart-bar mr-2"></i>
                    Generate by-Class Report
                </Button>
                <div className="text-sm text-gray-600 flex items-center">
                    {selectedLaneLineIds.size} lane line{selectedLaneLineIds.size !== 1 ? "s" : ""} selected
                </div>
                {validation.hasConflicts && conflictResolutions.size !== validation.conflicts.size && (
                    <div className="text-sm text-red-700 flex items-center">
                        <i className="fa fa-exclamation-circle mr-1"></i>
                        Please resolve all lane conflicts to generate report
                    </div>
                )}
            </div>

            {isLoadingReport && (
                <div className="mt-6 p-8 bg-gray-50 rounded border border-gray-200 text-center">
                    <i className="fa fa-spinner fa-spin text-3xl text-gray-400 mb-4"></i>
                    <p className="text-gray-600">Generating report...</p>
                </div>
            )}

            {reportError && (
                <div className="mt-6 bg-red-50 border border-red-300 rounded p-4">
                    <h3 className="text-red-800 font-semibold mb-2">
                        <i className="fa fa-exclamation-circle mr-2"></i>
                        Error Generating Report
                    </h3>
                    <p className="text-red-700">{reportError}</p>
                </div>
            )}
        </div>
    );
};

// ---------- Per-day rendering ----------

interface ClassDayMetrics {
    peakResult: ReturnType<typeof calculateRollingPeakHourGeneric>;
    peakStartHour: number;
    peakTimeLabel: string;
    hourlyTotals: Map<number, Map<string, number>>; // hour -> (columnKey -> count)
    dailyTotals: Map<string, number>; // columnKey -> daily total
    peakTotalsByCol: Map<string, number>; // columnKey -> peak-hour total
    dailyGrandTotal: number;
}

const computeClassDayMetrics = (dayRows: any[], columns: any[], classKey: ClassKey): ClassDayMetrics => {
    const peakResult = calculateRollingPeakHourGeneric(
        dayRows,
        (row) => (row.startTime ? new Date(row.startTime) : null),
        (row) => columns.reduce((sum: number, col: any) => sum + getCountForColumn(row, col.key, classKey), 0),
    );

    const hourlyTotals = new Map<number, Map<string, number>>();
    dayRows.forEach((row: any) => {
        if (!row.startTime) return;
        const hour = new Date(row.startTime).getHours();
        if (!hourlyTotals.has(hour)) hourlyTotals.set(hour, new Map());
        const h = hourlyTotals.get(hour)!;
        columns.forEach((col: any) => h.set(col.key, (h.get(col.key) || 0) + getCountForColumn(row, col.key, classKey)));
    });

    const dailyTotals = new Map<string, number>();
    columns.forEach((col: any) => dailyTotals.set(col.key, dayRows.reduce((sum: number, row: any) => sum + getCountForColumn(row, col.key, classKey), 0)));

    const peakTotalsByCol = new Map<string, number>();
    columns.forEach((col: any) => peakTotalsByCol.set(col.key, peakResult.peakRows.reduce((sum: number, row: any) => sum + getCountForColumn(row, col.key, classKey), 0)));

    return {
        peakResult,
        peakStartHour: peakResult.peakStartTime?.getHours() ?? -1,
        peakTimeLabel: formatPeakHourLabel(peakResult.peakStartTime, peakResult.peakEndTime),
        hourlyTotals,
        dailyTotals,
        peakTotalsByCol,
        dailyGrandTotal: Array.from(dailyTotals.values()).reduce((a, b) => a + b, 0),
    };
};

const panelDomId = (dayKey: string, classKey: ClassKey) => `tmc-panel-${classKey}-${dayKey.replace(/\s+/g, "-")}`;

interface DayPanelProps {
    dayKey: string;
    dayRows: any[];
    columns: any[];
    columnsByDirection: Map<number, any[]>;
    directionStreetNames: Map<number, string>;
    selectedHourByDayClass: Map<string, number>;
    setSelectedHourByDayClass: React.Dispatch<React.SetStateAction<Map<string, number>>>;
    collapsedPanels: Set<string>;
    setCollapsedPanels: React.Dispatch<React.SetStateAction<Set<string>>>;
}

const DayPanel: React.FC<DayPanelProps> = ({
    dayKey,
    dayRows,
    columns,
    columnsByDirection,
    directionStreetNames,
    selectedHourByDayClass,
    setSelectedHourByDayClass,
    collapsedPanels,
    setCollapsedPanels,
}) => {
    const metricsByClass: Record<ClassKey, ClassDayMetrics> = {
        light: computeClassDayMetrics(dayRows, columns, "light"),
        medium: computeClassDayMetrics(dayRows, columns, "medium"),
        heavy: computeClassDayMetrics(dayRows, columns, "heavy"),
    };

    const hours = Array.from(new Set(dayRows.filter((r) => r.startTime).map((r) => new Date(r.startTime).getHours()))).sort((a, b) => a - b);

    // Expand the matching detail panel and scroll it into view.
    const viewDetails = (classKey: ClassKey) => {
        const panelKey = `${dayKey}-${classKey}`;
        setCollapsedPanels((prev) => {
            const next = new Set(prev);
            next.delete(panelKey);
            return next;
        });
        setTimeout(() => {
            document.getElementById(panelDomId(dayKey, classKey))?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 50);
    };

    return (
        <section className="border-2 border-gray-300 rounded-xl p-4 md:p-6 bg-white">
            <header className="border-b border-gray-200 pb-3 mb-4">
                <h2 className="text-xl font-bold">
                    {new Date(dayKey).toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "short", day: "numeric" })}
                </h2>
            </header>

            {/* Class diagrams; wrap from 3 → 2 → 1 as the viewport narrows. */}
            <div className="grid gap-4 mb-6" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))" }}>
                {CLASS_SECTIONS.map((section) => {
                    const m = metricsByClass[section.key];
                    const hourKey = `${dayKey}-${section.key}`;
                    const selectedHour = selectedHourByDayClass.get(hourKey) ?? m.peakStartHour;
                    const hourTotals = selectedHour >= 0 ? m.hourlyTotals.get(selectedHour) || new Map<string, number>() : new Map<string, number>();
                    return (
                        <div key={section.key} className="border border-gray-200 rounded-lg p-3 bg-gray-50 flex flex-col">
                            <div className="text-center mb-2">
                                <h3 className="font-bold">{section.label}</h3>
                                <p className="text-xs text-gray-500">{section.subLabel}</p>
                                <p className="text-sm font-semibold mt-1">Daily total: {m.dailyGrandTotal.toLocaleString()}</p>
                                <p className="text-xs text-gray-600">Peak hour: {m.peakTimeLabel}</p>
                            </div>
                            <div className="flex items-center justify-center gap-2 mb-2 print:hidden">
                                <label className="text-xs font-medium">Hour:</label>
                                <select
                                    value={selectedHour}
                                    onChange={(e) => {
                                        const map = new Map(selectedHourByDayClass);
                                        map.set(hourKey, parseInt(e.target.value));
                                        setSelectedHourByDayClass(map);
                                    }}
                                    className="border border-gray-300 rounded px-2 py-1 text-xs"
                                >
                                    {hours.map((hour) => (
                                        <option key={hour} value={hour}>
                                            {String(hour).padStart(2, "0")}:00-{String((hour + 1) % 24).padStart(2, "0")}:00
                                            {hour === m.peakStartHour ? " (Peak)" : ""}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <IntersectionDiagram
                                compact
                                reportColumns={columns}
                                hourlyData={hourTotals}
                                dailyTotals={m.dailyTotals}
                                columnsByDirection={columnsByDirection}
                                directionStreetNames={directionStreetNames}
                            />
                            <button onClick={() => viewDetails(section.key)} className="mt-2 print:hidden">
                                <IconLink Icon={Icons.Expand}>View {section.label} details</IconLink>
                            </button>
                        </div>
                    );
                })}
            </div>

            {/* Collapsible per-class detail tables */}
            <div className="space-y-3">
                {CLASS_SECTIONS.map((section) => {
                    const panelKey = `${dayKey}-${section.key}`;
                    const collapsed = collapsedPanels.has(panelKey);
                    return (
                        <ClassTablePanel
                            key={panelKey}
                            domId={panelDomId(dayKey, section.key)}
                            collapsed={collapsed}
                            onToggle={() => {
                                const next = new Set(collapsedPanels);
                                if (collapsed) next.delete(panelKey);
                                else next.add(panelKey);
                                setCollapsedPanels(next);
                            }}
                            label={section.label}
                            classKey={section.key}
                            dayRows={dayRows}
                            columns={columns}
                            columnsByDirection={columnsByDirection}
                            directionStreetNames={directionStreetNames}
                            metrics={metricsByClass[section.key]}
                        />
                    );
                })}
            </div>
        </section>
    );
};

interface ClassTablePanelProps {
    domId: string;
    collapsed: boolean;
    onToggle: () => void;
    label: string;
    classKey: ClassKey;
    dayRows: any[];
    columns: any[];
    columnsByDirection: Map<number, any[]>;
    directionStreetNames: Map<number, string>;
    metrics: ClassDayMetrics;
}

const ClassTablePanel: React.FC<ClassTablePanelProps> = ({
    domId,
    collapsed,
    onToggle,
    label,
    classKey,
    dayRows,
    columns,
    columnsByDirection,
    directionStreetNames,
    metrics,
}) => {
    return (
        <div id={domId} className="border border-gray-200 rounded-lg overflow-hidden scroll-mt-4">
            <button onClick={onToggle} className="w-full flex items-center justify-between px-4 py-2 bg-gray-100 hover:bg-gray-200 text-left print:hidden">
                <span className="font-semibold">
                    <i className={`fa fa-chevron-${collapsed ? "right" : "down"} mr-2 text-gray-500`}></i>
                    {label}
                </span>
                <span className="text-sm text-gray-600">
                    Total {metrics.dailyGrandTotal.toLocaleString()} · Peak {metrics.peakTimeLabel}
                </span>
            </button>
            {/* Kept in the DOM when collapsed (hidden on screen) so it still prints. */}
            <div className={`${collapsed ? "hidden print:block" : "block"} overflow-x-auto p-2`}>
                <h4 className="hidden print:block font-semibold mb-2">{label}</h4>
                <table className="text-sm mx-auto rounded-lg shadow border border-gray-200 mb-2">
                    <thead className="bg-gray-100">
                        <tr className="bg-gray-50">
                            <th rowSpan={3} className="border-l border-r border-t border-gray-300 px-4 py-2 text-sm font-semibold text-left align-middle bg-gray-50">
                                Interval Start Time
                            </th>
                            {Array.from(columnsByDirection.entries()).map(([dir, cols]) => (
                                <th key={dir} colSpan={cols.length} className="border-l border-r border-t border-gray-300 px-2 py-2 text-sm font-semibold text-center bg-gray-50">
                                    {directionStreetNames.get(dir) || ""}
                                </th>
                            ))}
                            <th rowSpan={3} className="border-l border-r border-t border-gray-300 px-4 py-2 text-sm font-semibold text-center align-middle bg-gray-200">
                                Total
                            </th>
                        </tr>
                        <tr className="bg-gray-50">
                            {Array.from(columnsByDirection.entries()).map(([dir, cols]) => (
                                <th key={dir} colSpan={cols.length} className="border-l border-r border-gray-300 px-2 py-2 text-sm font-semibold text-center bg-gray-50">
                                    {getCardinalDirectionWithBound(dir)}
                                </th>
                            ))}
                        </tr>
                        <tr className="bg-gray-100 border-b border-gray-300">
                            {columns.map((col: any) => (
                                <th key={col.key} className="border-l border-r border-gray-300 px-2 py-2 text-xs font-semibold text-center bg-gray-100">
                                    {col.displayLabel}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {dayRows.map((row: any, idx: number) => {
                            const rowTotal = columns.reduce((sum: number, col: any) => sum + getCountForColumn(row, col.key, classKey), 0);
                            return (
                                <tr key={idx} className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                                    <td className="border border-gray-300 px-4 py-2 text-sm whitespace-nowrap">
                                        {row.startTime && new Date(row.startTime).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false })}
                                    </td>
                                    {columns.map((col: any) => (
                                        <td key={col.key} className="border border-gray-300 px-2 py-2 text-sm text-center">
                                            {getCountForColumn(row, col.key, classKey)}
                                        </td>
                                    ))}
                                    <td className="border border-gray-300 px-4 py-2 text-sm text-center font-semibold bg-gray-100">{rowTotal}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                    <tfoot>
                        <tr className="bg-gray-200 font-bold">
                            <td className="border border-gray-300 px-4 py-2 text-sm">Count Total</td>
                            {columns.map((col: any) => (
                                <td key={col.key} className="border border-gray-300 px-2 py-2 text-sm text-center">
                                    {metrics.dailyTotals.get(col.key) || 0}
                                </td>
                            ))}
                            <td className="border border-gray-300 px-4 py-2 text-sm text-center">{metrics.dailyGrandTotal}</td>
                        </tr>
                        <tr className="bg-yellow-50 font-semibold">
                            <td className="border border-gray-300 px-4 py-2 text-sm">Peak Hour ({metrics.peakTimeLabel})</td>
                            {columns.map((col: any) => (
                                <td key={col.key} className="border border-gray-300 px-2 py-2 text-sm text-center">
                                    {metrics.peakTotalsByCol.get(col.key) || 0}
                                </td>
                            ))}
                            <td className="border border-gray-300 px-4 py-2 text-sm text-center">{metrics.peakResult.peakTotal}</td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        </div>
    );
};

export const TurningMovementCountByClass = {
    name: "Turning Movement Count by Class",
    description: "TMC report broken into Light / Medium / Heavy using the FHWA 13-class default 3-bucket breakdown (1-3 / 4-7 / 8-13). Per day: the three class diagrams side by side, with collapsible detail tables below.",
    component: TMCByClassLoader,
    key: "tmc-by-class",
    defaultChartType: "other",
    defaultRange: DateOptions.LastWeek,
    config: {
        requiresMonth: false,
        allowsCustomRange: true,
        requiresYear: false,
        allowTargetTypeGrouping: false,
        maxDaysRange: 31,
    },
} as IConfiguredTrafficReport;
